import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"

/**
 * NativeShareController - Content sharing for native apps
 *
 * Uses native share sheet when in Hotwire Native app,
 * falls back to Web Share API or manual copy modal.
 *
 * Usage:
 *   <button data-controller="native-share"
 *           data-native-share-title-value="Check this out!"
 *           data-native-share-text-value="Found this on Toas2t"
 *           data-native-share-url-value="https://toas2t.com/products/123"
 *           data-action="native-share#share">
 *     Share
 *   </button>
 */
export default class extends Controller {
  static values = {
    title: String,
    text: String,
    url: String,
    imageUrl: String,
    fallbackUrl: { type: String, default: "/share/manual_copy_modal" }
  }

  connect() {
    this.isNative = NativeBridge.isNative()
  }

  /**
   * Share content
   */
  async share(event) {
    event.preventDefault()

    const shareData = {
      title: this.titleValue,
      text: this.textValue,
      url: this.urlValue || window.location.href
    }

    try {
      if (this.isNative) {
        await this.nativeShare(shareData)
      } else if (navigator.share) {
        await this.webShare(shareData)
      } else {
        this.fallbackShare(shareData)
      }

      this.dispatch("success", { detail: shareData })

    } catch (error) {
      // User cancelled is not an error
      if (error.name === "AbortError" || error.message === "User cancelled") {
        return
      }

      console.error("[NativeShare] Error:", error)
      this.dispatch("error", { detail: { error: error.message } })

      // Fall back to manual copy on error
      this.fallbackShare(shareData)
    }
  }

  /**
   * Use native share sheet
   */
  async nativeShare(shareData) {
    const options = { ...shareData }

    if (this.hasImageUrlValue) {
      let imageUrl = this.imageUrlValue
      // Convert relative URL to absolute URL for native bridge
      // iOS URLSession cannot download from relative paths
      if (imageUrl.startsWith('/')) {
        imageUrl = `${window.location.origin}${imageUrl}`
      }
      options.imageUri = imageUrl
    }

    const result = await NativeBridge.Share.share(options)
    return result
  }

  /**
   * Use Web Share API
   */
  async webShare(shareData) {
    await navigator.share(shareData)
  }

  /**
   * Fallback to manual copy modal
   */
  fallbackShare(shareData) {
    // Try clipboard API first
    if (navigator.clipboard) {
      const textToCopy = `${shareData.title}\n${shareData.text}\n${shareData.url}`

      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          this.showCopiedToast()
        })
        .catch(() => {
          this.openManualCopyModal(shareData)
        })
    } else {
      this.openManualCopyModal(shareData)
    }
  }

  /**
   * Show copied toast notification
   */
  showCopiedToast() {
    // Dispatch event for toast controller to handle
    this.dispatch("copied", {
      detail: { message: "已複製連結" },
      bubbles: true
    })
  }

  /**
   * Open manual copy modal (iOS Safari compatibility)
   */
  openManualCopyModal(shareData) {
    const url = new URL(this.fallbackUrlValue, window.location.origin)
    url.searchParams.set("url", shareData.url)
    url.searchParams.set("title", shareData.title)

    // Use Turbo to open modal
    Turbo.visit(url.toString(), { frame: "modal" })
  }
}
